import Router from 'next/router';
import { useAchievementTypes, useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { LinkButton, Owners, User } from 'components/common';
import { AchievementsTileContent } from '.';

import type { AchievementExtended } from 'shared-types';
import type { ButtonCategory, ButtonSize } from 'components/common/Button';

interface Props {
  achievement: Pick<
    AchievementExtended,
    'categories' | 'id' | 'owners' | 'title' | 'type'
  > &
    Partial<
      Pick<
        AchievementExtended,
        | 'numberOfActiveGroupCompletions'
        | 'numberOfPendingAccessRequests'
        | 'numberOfPendingAccessRequestsForSupervisors'
        | 'numberOfUsers'
        | 'isOwnerOrSuperadmin'
        | 'isSupervisor'
        | 'userAdminPermissions'
      >
    >;
}

const AchievementsAdminTile = ({ achievement }: Props) => {
  const { messages, theme, user } = useReduxState();

  const { isGroupAchievement } = useAchievementTypes({
    type: achievement?.type
  });

  const handleClick = () => {
    if (
      achievement.isOwnerOrSuperadmin ||
      achievement.userAdminPermissions?.length
    ) {
      Router.push(`/admin/achievements/${achievement.id}`);
    } else Router.push(`/admin/achievements/${achievement.id}/my-team`);
  };

  const handleCourseKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.target !== ev.currentTarget) return;
    // check if enter or spacebar
    if (ev.keyCode === 13 || ev.keyCode === 32) {
      ev.preventDefault();
      handleClick();
    }
  };

  const linkProps = {
    category: 'lighter' as ButtonCategory,
    isSemiRounded: true,
    onClick: (ev) => ev.stopPropagation(),
    size: 'small' as ButtonSize,
    style: {
      flexShrink: 0,
      fontSize: '1.3rem',
      width: 30,
      height: 30
    }
  };

  const usersMessage = isGroupAchievement
    ? getMessageWithPlaceholders({
        message: messages.achievementGroupConversationNumberOfConversations,
        placeholders: {
          numberOfConversations: achievement.numberOfActiveGroupCompletions
        }
      })
    : getMessageWithPlaceholders({
        message: messages.usersNumberOfUsers,
        placeholders: { numberOfUsers: achievement.numberOfUsers }
      });

  return (
    <div
      className="achievement"
      onClick={handleClick}
      onKeyDown={handleCourseKeyDown}
      role="button"
      tabIndex={0}
    >
      <div className="inner">
        <AchievementsTileContent achievement={achievement}>
          <div className="users">{usersMessage}</div>

          <div className="owners">
            {achievement.owners?.length ? (
              <>
                <User
                  showNameOrUsername={false}
                  size="tiny"
                  user={achievement.owners?.[0]}
                />

                <Owners owners={achievement.owners} />
              </>
            ) : (
              messages.commonNoOwners
            )}
          </div>
        </AchievementsTileContent>
      </div>

      <div className="actions">
        {achievement.isOwnerOrSuperadmin ||
        achievement.userAdminPermissions.length ? (
          isGroupAchievement ? (
            <LinkButton
              href={`/admin/achievements/${achievement.id}/group-conversations`}
              icon="user-friends"
              tooltip={messages.achievementGroupConversations}
              {...linkProps}
            />
          ) : (
            <LinkButton
              href={`/admin/achievements/${achievement.id}/users`}
              icon="user"
              tooltip={messages.commonUsers}
              unreadItems={achievement.numberOfPendingAccessRequests}
              {...linkProps}
            />
          )
        ) : null}

        {achievement.isSupervisor && !user?.roles?.includes('superadmin') ? (
          <LinkButton
            href={`/admin/achievements/${achievement.id}/my-team`}
            icon="sitemap"
            tooltip={messages.myTeamPage}
            unreadItems={
              achievement.numberOfPendingAccessRequestsForSupervisors
            }
            {...linkProps}
          />
        ) : null}

        {achievement.isOwnerOrSuperadmin ? (
          <>
            {user?.roles.includes('superadmin') ? (
              <LinkButton
                href={`/admin/achievements/${achievement.id}/supervisors`}
                icon="sitemap"
                tooltip={messages.adminSupervisors}
                {...linkProps}
              />
            ) : null}
            <LinkButton
              href={`/admin/achievements/${achievement.id}/settings`}
              icon="cog"
              tooltip={messages.commonSettings}
              {...linkProps}
            />
            <LinkButton
              href={`/admin/achievements/${achievement.id}/completion-settings`}
              icon="check-circle"
              tooltip={messages.achievementCompletionDetails}
              {...linkProps}
            />
            <LinkButton
              href={`/admin/achievements/${achievement.id}/access`}
              icon="eye"
              tooltip={messages.eventCalendarSettingsAccess}
              {...linkProps}
            />
            <LinkButton
              href={`/admin/achievements/${achievement.id}/events`}
              icon="list"
              tooltip={messages.commonActivityLog}
              {...linkProps}
            />
            <LinkButton
              href={`/admin/achievements/${achievement.id}/manage`}
              icon="tools"
              tooltip={messages.pagesManage}
              {...linkProps}
            />
          </>
        ) : null}
      </div>

      <style jsx>{`
        .achievement {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          box-shadow: ${theme.cardBoxShadow};
          cursor: pointer;
          display: flex;
          flex-direction: column;
          height: 100%;
          transition: box-shadow 0.3s;
        }
        .achievement:focus,
        .achievement:hover {
          box-shadow: ${theme.cardBoxShadowActive};
        }
        .inner {
          flex-grow: 1;
          padding: 15px;
        }
        .users {
          color: ${theme.textColorLight};
          font-size: 1.4rem;
          font-weight: 600;
          margin-top: 5px;
        }
        .owners {
          align-items: center;
          color: ${theme.textColorLight};
          display: inline-flex;
          flex-shrink: 0;
          font-size: 1.3rem;
          font-weight: 600;
          gap: 5px;
          margin-top: auto;
          padding-top: 15px;
          padding-bottom: 2px;
        }
        .actions {
          border-top: 1px solid ${theme.lightGrey};
          display: flex;
          flex-grow: 0;
          flex-shrink: 0;
          gap: 10px;
          padding: 10px 15px;
        }
      `}</style>
    </div>
  );
};

export default AchievementsAdminTile;
